import {
  INTERFACE_APPEARANCE_CONTRACT,
  datasetKeyForAttribute,
  interfaceAppearanceTargets,
} from './interfaceAppearanceContract.js'

const activeOverrideByRoot = new WeakMap()

// Every DOM name this bridge may write comes from the contract, so a target can
// never drift away from the declaration the tests check it against.
const OWNED = INTERFACE_APPEARANCE_CONTRACT.INTERFACE_OWNED
const FONT_FAMILY_VAR = OWNED.font_family.target
const INTERFACE_FONT_ACTIVE_KEY = datasetKeyForAttribute('data-interface-font-active')
const INTERFACE_RADIUS_ACTIVE_KEY = datasetKeyForAttribute('data-interface-radius-active')
const ICON_STYLE_KEY = datasetKeyForAttribute(OWNED.icon_set.target)
const BUTTON_SKIN_KEY = datasetKeyForAttribute(OWNED.button_skin.target)
const RADIUS_STEP_VARS = Object.fromEntries(
  Object.entries(interfaceAppearanceTargets('style'))
    .filter(([field]) => field.startsWith('radius.'))
    .map(([field, cssVar]) => [field.slice('radius.'.length), cssVar]),
)

// A font-family value is written verbatim into a CSS declaration, so accept
// only what a family list can legitimately contain: names, quotes, commas and
// hyphens. This rejects a declaration break (`;`), a comment (`/*`), an at-rule
// and `url(...)` rather than trusting CSSOM to discard them silently.
const FONT_FAMILY_PATTERN = /^[A-Za-z0-9 _'"\-,.]+$/
const FONT_FAMILY_MAX_LENGTH = 200
const RADIUS_LENGTH_PATTERN = /^(?:0|(?:\d+|\d*\.\d+)(?:px|rem|em|%))$/

/** Map a preset icon_set onto the existing office icon-style vocabulary. */
export function resolveInterfaceIconStyle(themeConfig) {
  if (!themeConfig || themeConfig.icon_set == null || themeConfig.icon_set === '') return null
  const iconSet = String(themeConfig.icon_set).trim().toLowerCase()
  if (iconSet === 'paper') return 'paper'
  if (iconSet === '3d' || iconSet === 'premium' || iconSet === 'color3d') return 'color3d'
  // MDI, flat, and named line/asset packs keep their existing flat behavior.
  return 'flat'
}

/** Accept only the documented interface-owned button skins. */
export function resolveInterfaceButtonSkin(themeConfig) {
  const skin = String(themeConfig?.button_skin || '').trim().toLowerCase()
  return skin === 'classic' || skin === 'premium' ? skin : null
}

/**
 * Resolve `theme_config.font_family` into a CSS font-family value.
 *
 * The value is used verbatim, and no web font is fetched for it, so a preset
 * should declare a full stack ending in a family that always resolves.
 */
export function resolveInterfaceFontFamily(themeConfig) {
  const raw = themeConfig?.font_family
  if (typeof raw !== 'string') return null
  const value = raw.trim()
  if (!value || value.length > FONT_FAMILY_MAX_LENGTH) return null
  return FONT_FAMILY_PATTERN.test(value) ? value : null
}

/** Normalise one radius step: a non-negative px number or a CSS length. */
function resolveRadiusLength(value) {
  if (typeof value === 'number') {
    return Number.isFinite(value) && value >= 0 ? `${value}px` : null
  }
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return RADIUS_LENGTH_PATTERN.test(trimmed) ? trimmed : null
}

/**
 * Resolve `theme_config.radius` into its office custom properties.
 *
 * Shape: an object with optional `sm` / `md` / `lg` steps. Each step is
 * independent, so declaring only `md` leaves the small and large office radii
 * in place. Returns null when no step is usable.
 */
export function resolveInterfaceRadius(themeConfig) {
  const raw = themeConfig?.radius
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
  const resolved = {}
  for (const [step, cssVar] of Object.entries(RADIUS_STEP_VARS)) {
    const length = resolveRadiusLength(raw[step])
    if (length) resolved[cssVar] = length
  }
  return Object.keys(resolved).length ? resolved : null
}

// Which Vuetify component default stands between a radius step and the
// element that should wear it. The office `defaults` in plugins/vuetify.js
// give these components a `rounded-*` utility class, and that class carries
// `!important`, so a preset token can never win against it from CSS. Dropping
// the default for the steps a preset declares lets the token apply, while a
// component that asks for a radius explicitly in its template still overrides
// both. Chips are deliberately absent: every pack draws them as pills.
const RADIUS_STEP_COMPONENTS = {
  '--brand-radius-lg': 'VCard',
  '--brand-radius-md': 'VBtn',
  '--brand-radius-sm': 'VTextField',
}

/**
 * Resolve the Vuetify component defaults a preset needs so its declared radius
 * steps are not overridden by the office rounding. Returns null when the
 * preset declares no radius, leaving every office default untouched.
 */
export function resolveInterfaceComponentDefaults(themeConfig) {
  const radius = resolveInterfaceRadius(themeConfig)
  if (!radius) return null
  const defaults = {}
  for (const cssVar of Object.keys(radius)) {
    const component = RADIUS_STEP_COMPONENTS[cssVar]
    // `undefined` means "no default", which is what leaves the element with no
    // rounding class at all so the token applies. `false` would NOT do this:
    // Vuetify's useRounded reads it as an explicit request for square corners
    // and emits `rounded-0`.
    if (component) defaults[component] = { rounded: undefined }
  }
  return Object.keys(defaults).length ? defaults : null
}

// Marks a default this preset introduced, so restoring removes the key
// instead of writing `undefined` over an office default that never existed.
const DEFAULT_WAS_ABSENT = Symbol('interface component default absent')

const previousComponentDefaults = new WeakMap()

/**
 * Apply component defaults on Vuetify's ROOT defaults for the technician
 * session, and restore the office values exactly when it ends.
 *
 * A `v-defaults-provider` around the shell cannot reach a dialog: VDialog
 * wraps its content in `<VDefaultsProvider root="VDialog">`, which walks back
 * past every local provider to the root defaults. The root is therefore the
 * only layer both the shell and its dialogs read, which is why this writes
 * there and why it must put back exactly what it found.
 */
export function applyInterfaceComponentDefaults(defaultsRef, componentDefaults) {
  if (!defaultsRef || typeof defaultsRef !== 'object') return
  const store = { ...(defaultsRef.value || {}) }

  const previous = previousComponentDefaults.get(defaultsRef)
  if (previous) {
    for (const [component, { props, componentWasAbsent }] of Object.entries(previous)) {
      const target = { ...(store[component] || {}) }
      for (const [prop, value] of Object.entries(props)) {
        if (value === DEFAULT_WAS_ABSENT) delete target[prop]
        else target[prop] = value
      }
      // A component the office never configured must disappear again rather
      // than linger as an empty entry.
      if (componentWasAbsent && !Object.keys(target).length) delete store[component]
      else store[component] = target
    }
    previousComponentDefaults.delete(defaultsRef)
  }

  if (componentDefaults) {
    const snapshot = {}
    for (const [component, props] of Object.entries(componentDefaults)) {
      const componentWasAbsent = !(component in store)
      const target = { ...(store[component] || {}) }
      const restored = {}
      for (const [prop, value] of Object.entries(props)) {
        restored[prop] = prop in target ? target[prop] : DEFAULT_WAS_ABSENT
        target[prop] = value
      }
      snapshot[component] = { props: restored, componentWasAbsent }
      store[component] = target
    }
    previousComponentDefaults.set(defaultsRef, snapshot)
  }

  defaultsRef.value = store
}

/**
 * Resolve every declared appearance field, or null when a preset declares none
 * of them and therefore inherits the office appearance whole.
 */
export function resolveInterfaceAppearanceOverride(themeConfig) {
  const override = {
    iconStyle: resolveInterfaceIconStyle(themeConfig),
    buttonSkin: resolveInterfaceButtonSkin(themeConfig),
    fontFamily: resolveInterfaceFontFamily(themeConfig),
    radius: resolveInterfaceRadius(themeConfig),
  }
  return Object.values(override).some(Boolean) ? override : null
}

/** Reassert the active interface after branding rewrites its office base. */
export function reapplyActiveInterfaceAppearance(root) {
  if (!root) return
  const active = activeOverrideByRoot.get(root)
  if (!active) return

  if (active.iconStyle) root.dataset[ICON_STYLE_KEY] = active.iconStyle
  if (active.buttonSkin === 'premium') root.dataset[BUTTON_SKIN_KEY] = 'premium'
  if (active.buttonSkin === 'classic') delete root.dataset[BUTTON_SKIN_KEY]
  if (active.fontFamily) root.dataset[INTERFACE_FONT_ACTIVE_KEY] = 'true'
  if (active.radius) root.dataset[INTERFACE_RADIUS_ACTIVE_KEY] = 'true'

  if (!root.style) return
  if (active.fontFamily) root.style.setProperty(FONT_FAMILY_VAR, active.fontFamily)
  for (const [cssVar, length] of Object.entries(active.radius || {})) {
    root.style.setProperty(cssVar, length)
  }
}

/**
 * Apply explicit interface appearance fields globally for the technician
 * session. Global scope is required for Vuetify overlays teleported to body.
 * Missing fields deliberately retain the current office/personal fallback.
 *
 * The bridge only ever overwrites keys the branding store itself writes, so
 * restoration is a single branding pass and can leave no key behind.
 */
export function applyInterfaceAppearanceToRoot(root, themeConfig, active, restoreBranding) {
  if (!root) return
  const previous = activeOverrideByRoot.get(root)
  activeOverrideByRoot.delete(root)
  delete root.dataset[INTERFACE_FONT_ACTIVE_KEY]
  delete root.dataset[INTERFACE_RADIUS_ACTIVE_KEY]

  if (previous && typeof restoreBranding === 'function') restoreBranding()
  if (!active) return

  const override = resolveInterfaceAppearanceOverride(themeConfig)
  if (!override) return

  // Start from the latest branding state so fields omitted by the interface
  // continue to inherit exactly as they did before this override existed.
  if (!previous && typeof restoreBranding === 'function') restoreBranding()
  activeOverrideByRoot.set(root, override)
  reapplyActiveInterfaceAppearance(root)
}
